import { DatePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import { useCommaNumberInput } from '../../../../../../../hooks/useInputFormat';
import {
  formatCurrencyForAntd,
  parseCurrencyFromAntd,
} from '../../../../../../../utils/currencyFormat';
import { OwnershipStatus } from '../../../../../../../types/apiTypes';
import { FLEET_POLICY_LABEL } from '../fleet-insurance/fleetInsurance';
import AssetFormField from './AssetFormField';
import { AssetFormData, AssetType, FleetInsuranceAware } from './types';

/**
 * The insurance input's classes live here rather than inline because fleet mode
 * needs a second variant: built by interpolation so the separating space cannot
 * go missing the way it did on the pending-truck card, and as whole strings so
 * Prettier's class sorter has nothing to trim.
 */
/**
 * The ownership choices, driven off the generated `OwnershipStatus` enum so the
 * dropdown cannot drift from the Joi `.valid(...)` the create and patch
 * handlers validate against. Edit mode used to render its own list —
 * owned/leaseToOwn/leased/contracted — which offered two values the API rejects
 * and hid `financing`, the one it accepts (NODE-AWSLAMBDA-X0). Both modes now
 * render the same set.
 */
const OWNERSHIP_LABELS: Record<OwnershipStatus, string> = {
  [OwnershipStatus.Owned]: 'Owned',
  [OwnershipStatus.Leased]: 'Leased',
  [OwnershipStatus.Financing]: 'Financing',
};
const OWNERSHIP_OPTIONS = Object.values(OwnershipStatus);

const INSURANCE_INPUT_CLASS =
  'mt-1 w-full rounded-md border border-gray-300 p-1 focus:outline-none';
const INSURANCE_INPUT_LOCKED_CLASS = `${INSURANCE_INPUT_CLASS} bg-[#F5F6F7] text-graysecondary`;

interface AssetFinancialsProps extends FleetInsuranceAware {
  register: UseFormRegister<AssetFormData>;
  control: Control<AssetFormData>;
  errors: FieldErrors<AssetFormData>;
  assetType: AssetType;
}

/**
 * Operational "left column": ownership status, payment + insurance amounts, and
 * (truck only) current/annual mileage and the optional PM/repair dates. The
 * truck-only gating and validation rules are copied verbatim from the legacy
 * forms. `ownershipStatus` no longer varies by mode: the per-mode lists were
 * how edit came to offer values the API rejects (see OWNERSHIP_OPTIONS), so
 * both modes now render the backend enum. The component therefore no longer
 * takes `mode`.
 *
 * THT-675/R3.2: when the customer is on a single fleet-wide policy the
 * per-asset insurance field stays on screen, disabled and empty, with "Covered
 * by fleet policy" underneath — the same shape the pending-truck card and the
 * asset details row already use. It used to be removed entirely, which made
 * this the one surface that explained nothing.
 *
 * Blanking is a RENDER decision only: the form still holds the amount seeded
 * from the asset and `useAssetForm.toSubmitValues` sends the stored value back,
 * so nothing on this screen can erase a premium. Validation is dropped while
 * the field is disabled — an empty field the customer cannot fill in must not
 * block the save.
 */
export default function AssetFinancials({
  register,
  control,
  errors,
  assetType,
  isFleetInsurance,
}: AssetFinancialsProps) {
  const commaInput = useCommaNumberInput<AssetFormData>(register);

  return (
    <div className="flex w-full flex-col items-start space-y-3 md:w-1/2">
      <AssetFormField
        id="ownershipStatus"
        label="Ownership Status"
        error={errors.ownershipStatus?.message}
      >
        <select
          id="ownershipStatus"
          {...register('ownershipStatus', {
            required: 'Ownership status is required',
          })}
          className={`mt-1 block w-full border px-3 py-2 ${
            errors.ownershipStatus ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
        >
          {OWNERSHIP_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {OWNERSHIP_LABELS[value]}
            </option>
          ))}
        </select>
      </AssetFormField>

      <AssetFormField
        id="assetPayment"
        label={`${
          assetType === 'truck' ? 'Truck' : 'Trailer'
        } Payment Amount (USD per month)`}
        error={errors.assetPayment?.message}
      >
        <Controller
          name="assetPayment"
          control={control}
          rules={{
            required: 'Asset Payment is required',
            min: { value: 0, message: 'Asset payment must be ≥ 0' },
          }}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <InputNumber
              id="assetPayment"
              className="mt-1 w-full rounded-md border border-gray-300 p-1 focus:outline-none"
              placeholder="Ex. $ 100,000"
              formatter={(v) => formatCurrencyForAntd(v)}
              parser={(v) => parseCurrencyFromAntd(v)}
              min={0}
              controls={false}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={errors.assetPayment ? 'error' : undefined}
              ref={ref}
            />
          )}
        />
      </AssetFormField>

      <AssetFormField
        id="insurancePayment"
        label="Insurance Payments Amount (USD per month)"
        error={isFleetInsurance ? undefined : errors.insurancePayment?.message}
      >
        <>
          <Controller
            name="insurancePayment"
            control={control}
            rules={
              isFleetInsurance
                ? {}
                : {
                    required: 'Insurance Payment is required',
                    min: { value: 0, message: 'Insurance payment must be ≥ 0' },
                  }
            }
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <InputNumber
                id="insurancePayment"
                className={
                  isFleetInsurance
                    ? INSURANCE_INPUT_LOCKED_CLASS
                    : INSURANCE_INPUT_CLASS
                }
                placeholder={isFleetInsurance ? '' : 'Ex. $ 100,000'}
                formatter={(v) => formatCurrencyForAntd(v)}
                parser={(v) => parseCurrencyFromAntd(v)}
                min={0}
                controls={false}
                disabled={isFleetInsurance}
                // The stored amount is deliberately not shown (R3.2 — "we don't
                // need to show the old number"); it is still in form state.
                value={isFleetInsurance ? null : value}
                onChange={onChange}
                onBlur={onBlur}
                status={errors.insurancePayment ? 'error' : undefined}
                ref={ref}
              />
            )}
          />
          {isFleetInsurance && (
            <p className="mt-1 text-[12px] text-graysecondary">
              {FLEET_POLICY_LABEL}
            </p>
          )}
        </>
      </AssetFormField>

      {assetType === 'truck' && (
        <AssetFormField
          id="assetMileage"
          label="Current Mileage"
          placeholder="Ex. 200,000"
          inputMode="numeric"
          error={errors.assetMileage?.message}
          registration={commaInput('assetMileage', {
            required: 'Current asset mileage is required',
            pattern: {
              value: /^[0-9,]*$/,
              message: 'Invalid Asset Mileage, must be digits',
            },
          })}
        />
      )}

      {assetType === 'truck' && (
        <AssetFormField
          id="typicalAnnualMileage"
          label="Typical Annual Mileage on Road"
          placeholder="Ex. 100,000"
          inputMode="numeric"
          error={errors.typicalAnnualMileage?.message}
          registration={commaInput('typicalAnnualMileage', {
            required: 'Typical Annual Mileage is required',
            pattern: {
              value: /^[0-9,]*$/,
              message: 'Invalid annual mileage, must be digits',
            },
          })}
        />
      )}

      {assetType === 'truck' && (
        <AssetFormField id="dateOfLastPM" label="Date of Last PM (optional)">
          <Controller
            name="dateOfLastPM"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DatePicker
                id="dateOfLastPM"
                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                format="MM/DD/YYYY"
                value={value ? dayjs(value) : null}
                onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : '')}
                onBlur={onBlur}
              />
            )}
          />
        </AssetFormField>
      )}

      {assetType === 'truck' && (
        <AssetFormField
          id="dateOfLastRepair"
          label="Date of Last Repair (optional)"
        >
          <Controller
            name="dateOfLastRepair"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DatePicker
                id="dateOfLastRepair"
                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                format="MM/DD/YYYY"
                value={value ? dayjs(value) : null}
                onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : '')}
                onBlur={onBlur}
              />
            )}
          />
        </AssetFormField>
      )}
    </div>
  );
}
